import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';

import { SendTypes, UseTypes } from 'src/app/core/api/models';
import { EventTypes } from 'src/app/core/constants/event-types';
import { TabsDynamicComponent } from 'src/app/layout/components/tabs-dynamic/tabs-dynamic.component';
import { GetScheduledMessageByIdDto } from '../../../core/api/models/get-scheduled-message-by-id-dto';
import { PhoneBookRecipientRequest } from '../../../core/api/models/phone-book-recipient-request';
import { PrivateNumbersService } from '../../../core/api/services/private-numbers.service';
import { ScheduledMessagesService } from '../../../core/api/services/scheduled-messages.service';
import { FormPageConfig } from '../../../core/models/form-page-config.model';
import { DialogService } from '../../../core/services/dialog.service';
import { DynamicTabService } from '../../../core/services/dynamic-tab.service';
import { UtilityService } from '../../../core/services/utility.service';
import { PhonebookGlobalIndex } from '../../facility/phonebook/shared/phonebook-custom.model';
import { PhonebookStateService } from '../../facility/phonebook/shared/phonebook-state.service';
import { BaseFormPageComponent } from '../../shared/base/components/base-form-page.component';
import { CreateDynamicScheduledMessageCommand } from './../../../core/api/models/create-dynamic-scheduled-message-command';
import { DateTimeConfig } from './../../../shared/components/date-time-picker/date-time-picker.component';

@Component({
    selector: 'app-dynamic-send-sms',
    templateUrl: './dynamic-send-sms.component.html',
    styleUrls: ['../base/base-sending-system.component.scss'],
    standalone: false
})
export class DynamicSendSmsComponent
  extends BaseFormPageComponent
  implements OnInit, OnDestroy
{
  phonebookStateService = inject(PhonebookStateService);

  //#region properties

  tabName: string = 'dynamicSendSms';
  model: CreateDynamicScheduledMessageCommand = {};
  formPageConfig: FormPageConfig<GetScheduledMessageByIdDto> = {};
  directDateTimeConfig: DateTimeConfig;
  title: string = '';
  refreshPhonebook: number = 0;
  smsCount: number;
  isVisibleHelpPage: boolean = false;

  get SendTypes() {
    return SendTypes;
  }
  get UseTypes() {
    return UseTypes;
  }

  sendLoading: boolean;
  isCanSendFlashSms: boolean = false;

  //#endregion

  //#endregion

  constructor(
    protected currentService: ScheduledMessagesService,
    public dialogService: DialogService,
    public utilityService: UtilityService,
    protected translate: TranslateService,
    public dynamicTabService: DynamicTabService,
    public privateNumberService: PrivateNumbersService,
    private tabsDynamicComponent: TabsDynamicComponent
  ) {
    super();
    this.baseCreateGetApiForSend(
      this.currentService,
      'scheduledMessagesCreateDynamicScheduledMessageGet'
    );
  }

  ngOnInit() {
    try {
      this.model.phoneBookRecipientRequests = [];
      super.ngOnInit();

      this.pageVocabs = 'pages.dynamicSendSms.';
      this.fields = this.pageVocabs + 'form.fields.';
      this.title = this.pageVocabs + 'title';
      this.configDirectDateTimeComponent();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  closeThisTab() {
    try {
      const TimeOut = setTimeout(() => {
        this.tabsDynamicComponent.manualCloseTab('dynamicSendSms');
        clearTimeout(TimeOut);
      }, 50);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  //#region init

  configDirectDateTimeComponent() {
    try {
      this.directDateTimeConfig = {
        dateLabel: 'common.sendDay',
        timeLabel: 'common.sendHour',
        dateFormControl: this.form?.get('sendDate') as FormControl,
        timeFormControl: this.form?.get('sendTime') as FormControl,
        minDateType: 'today',
        isDefaultValue: true,
        responsiveClassName: 'md:tw-w-1/2',
        isEditMode: this.formPageConfig.isEditMode,
      };
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  initForm() {
    this.form = new FormGroup({
      senderNumberId: new FormControl('', Validators.required),
      dynamicTemplateId: new FormControl('', Validators.required),
      sendDate: new FormControl('', Validators.required),
      sendTime: new FormControl('', Validators.required),
      description: new FormControl(''),
      isFlashSms: new FormControl(''),
    });
  }

  //#region Api

  //create
  baseCreateApi(model: CreateDynamicScheduledMessageCommand) {
    this.unSubs();

    this.loading = true;
    this.sendLoading = true;

    this.currentService
      .scheduledMessagesCreateDynamicScheduledMessagePost({ body: model })
      .pipe(
        this.basePipeOperators(),
        finalize(() => (this.sendLoading = false))
      )
      .subscribe((result) => {
        try {
          this.apiNotificationService.notifyMessage({
            result,
            mode: 'matDialog',
          });
          if (result.isSucceed) {
            this.onSuccess();
            this.refreshPage();
          }
        } catch (error) {
          this.loggerService.logError(
            this.tagName,
            error,
            EventTypes.sendingSystem
          );
        }
      });
  }

  //#endregion

  //#region phonebook

  getPhonebookRecipients(model: PhoneBookRecipientRequest[]) {
    this.model.phoneBookRecipientRequests = model;
  }

  getGlobalIndex(event: PhonebookGlobalIndex) {
    try {
      if (event.count || event.fromIndex) {
        this.model.scheduledMessagesFromIndex = event.fromIndex;
        this.model.scheduledMessagesCount = event.count;
      } else {
        this.model.scheduledMessagesCount = null;
        this.model.scheduledMessagesFromIndex = null;
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  onRefreshPhonebook() {
    try {
      this.refreshPhonebook++;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  //#endregion

  onSubmit() {
    try {
      if (
        !this.phonebookStateService.checkForEmptyRecipients(
          this.model?.phoneBookRecipientRequests
        )
      )
        return;
      if (!this.isValidForm(this.form, this.tagName)) return;

      if (this.loading) return;

      this.baseCreateApi(this.model);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  unSubs() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  openPage(tabName) {
    try {
      this.dynamicTabService.setNewTab('');
      this.dynamicTabService.setNewTab(tabName);
      this.dynamicTabService.setActiveTab(tabName);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }

  onShowHelpPage() {
    this.isVisibleHelpPage = true;
  }
  onHideHelpPage() {
    this.isVisibleHelpPage = false;
  }
  refreshPage() {
    try {
      this.tabsDynamicComponent.refreshTab(this.tabName);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.sendingSystem
      );
    }
  }
}
